import React from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Tag, Typography } from 'antd';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkMath from 'remark-math';
import 'katex/dist/katex.min.css';

const RESTRAINT_EXPLANATION = String.raw`
The restraint score rewards the user for keeping a slider near zero when the
assessment answer-key view for an instrument is unclear.

### 1. Decide when the view is unclear

An **ICS — Individual Conviction Score** is one news item's signed impact on
one instrument. **PCS — Period Conviction Score** is the signed sum of those
impacts for the period. PM converts PCS onto the same $-5$ to $+5$ scale as the
slider before checking its size.

The assessment answer key stores **Agreement**, generated by comparing the
signed sum with the total absolute size of all the impacts:

$$
A=\frac{\left|\sum_i \mathrm{ICS}_i\right|}
        {\sum_i\left|\mathrm{ICS}_i\right|}.
$$

Agreement is near $1$ when the news points the same way and near $0$ when
positive and negative impacts cancel. This is different from a small PCS: all
the impacts can agree but still be small. PM reads this stored value during
scoring; it does not recalculate Agreement from the news rows.

PM scores restraint when either of these checks is true:

- **Small combined view:** converted PCS is below $0.35$ in absolute value.
- **Opposing news impacts:** Agreement is below $0.35$.

### 2. Score the slider restraint

PM uses the last slider value saved for the instrument. If it was never moved,
its value is $0$. For slider value $s_i$, its restraint score $r_i$ is:

$$
r_i=\max\!\left(0,\min\!\left(1,1-\frac{|s_i|}{4}\right)\right).
$$

- Slider $0$: $100\%$ credit.
- Slider $\pm1$: $75\%$ credit.
- Slider $\pm2$: $50\%$ credit.
- Slider $\pm3$: $25\%$ credit.
- Slider $\pm4$ or $\pm5$: no credit.

### 3. Convert the score to Pillar 1 points

The restraint scores are averaged equally across the instruments that met at
least one unclear-view check. $N$ is the number of those instruments:

$$
\mathrm{RestraintPoints}=10\left(\frac{\sum_i r_i}{N}\right).
$$

For example, sliders $0$ and $\pm2$ score $1.00$ and $0.50$, producing
$7.5$ of the available $10$ points. If no instruments meet either check, the
component receives full credit.
`;

const PillarOneRestraintGuide: React.FC = () => (
  <aside
    aria-labelledby="pillar-one-restraint-title"
    className="assessment-inspect-assessment__pillar-one-guide"
  >
    <header className="assessment-inspect-assessment__pillar-one-guide-header">
      <div className="assessment-inspect-assessment__pillar-one-guide-kicker">
        <span>
          <InfoCircleOutlined aria-hidden="true" /> Scoring guide
        </span>
        <Tag color="blue">10% of Pillar 1</Tag>
      </div>
      <Typography.Title id="pillar-one-restraint-title" level={4}>
        Restraint when unclear score
      </Typography.Title>
      <Typography.Text type="secondary">
        How keeping sliders near zero can add up to 10 points per period.
      </Typography.Text>
    </header>

    <div className="assessment-inspect-assessment__pillar-one-guide-body">
      <ReactMarkdown rehypePlugins={[rehypeKatex]} remarkPlugins={[remarkMath]}>
        {RESTRAINT_EXPLANATION}
      </ReactMarkdown>
    </div>
  </aside>
);

export default PillarOneRestraintGuide;
